import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, ReplaySubject, takeUntil } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';
import SimpleColorConfiguration from 'entity/SimpleColorConfiguration';
import { IncomingMessage, IncomingMessageType, RingColor } from 'messages/websocket.messages';
import BadgingColorController from 'rest/BadgingColorController';

import { WebsocketService } from '../websocket.service';

export interface VisualIndicatorInfo {
  colors?: RingColor[];
  indicatorLocation?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RingColorsService extends Loggable {
  private websocketService = inject(WebsocketService);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  private ringColorsStreamSource = new BehaviorSubject<VisualIndicatorInfo>({});
  public ringColorsStream$ = this.ringColorsStreamSource.asObservable();

  private badgingColorController = new BadgingColorController();

  public constructor() {
    super();
    this.setDisplayName('RingColorsService');

    this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleWebsocketMessage);
  }

  public getColorPalette(colorPaletteId: string) {
    return this.badgingColorController.getColorPalette(colorPaletteId);
  }

  public getColorPaletteConfig(socialRoomSetId: string) {
    return this.badgingColorController.getSimpleColorConfiguration(socialRoomSetId);
  }

  public getColorPalettes() {
    return this.badgingColorController.getColorPalettes();
  }

  public getSimpleColorConfiguration(socialRoomSetId: string) {
    return this.badgingColorController.getSimpleColorConfiguration(socialRoomSetId);
  }

  public saveSimpleColorConfiguration(socialRoomSetId: string, config: SimpleColorConfiguration) {
    return this.badgingColorController.saveSimpleColorConfiguration(socialRoomSetId, config);
  }

  public removeSimpleColorConfiguration(socialRoomSetId: string) {
    return this.badgingColorController.removeSimpleColorConfiguration(socialRoomSetId);
  }

  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.RING_COLORS: {
        if (msg.colors) {
          this.ringColorsStreamSource.next({ colors: msg.colors, indicatorLocation: msg.indicatorLocation });
        }
        return;
      }
    }
  };
}
